import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableRow,
    Paper,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Select,
    MenuItem,
    Chip,
    InputLabel,
    FormControl,
    CircularProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import { staffAuthHeaders, staffSignInNotice } from '../lib/staffAuthHeaders';
import moment from 'moment-timezone';


const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'https://api.admin.marsreel.com';

const OverviewContainer = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
});

const Title = styled(Typography)({
    fontSize: '1.8rem',
    color: '#fff',
    fontWeight: 'bold',
    marginBottom: '20px',
});

const StyledTable = styled(Table)({
    backgroundColor: '#202020',
    borderRadius: '12px',
});

const TableBodyCell = styled(TableCell)({
    color: '#fff',
    fontSize: '1rem',
    padding: '16px',
});

const TableHeaderCell = styled(TableCell)({
    fontSize: '1rem',
    fontWeight: 'bold',
    color: '#12c099',
    backgroundColor: '#1c1c1c',
    padding: '16px',
});

const ApplicantOverview = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [applicant, setApplicant] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [isInitiating, setIsInitiating] = useState(false);
    const [editForm, setEditForm] = useState({
        name: '',
        email: '',
        phone: '',
        role: '',
        hourlyRate: '',
        city: '',
        state: '',
        zip: '',
        availability: {},
        vehicleType: '',
        compensationType: '',
    });
    const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);

    useEffect(() => {
        if (applicant) {
            const userTimezone = applicant.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
            const dayMap = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

            // Group the array of slots into an object by day name
            const availabilityForEdit = (applicant.availability || []).reduce((acc, slot) => {
                const dayName = dayMap[slot.dayOfWeek];
                if (!acc[dayName]) {
                    acc[dayName] = [];
                }
                const formattedSlot = {
                    start: moment.utc(slot.startTimeUTC, "HH:mm").tz(userTimezone).format("h:mm A"),
                    end: moment.utc(slot.endTimeUTC, "HH:mm").tz(userTimezone).format("h:mm A"),
                };
                acc[dayName].push(formattedSlot);
                return acc;
            }, {});

            setEditForm({
                name: applicant.name,
                email: applicant.email,
                phone: applicant.phone,
                role: applicant.role,
                hourlyRate: applicant.hourlyRate || '',
                city: applicant.address?.city || '',
                state: applicant.address?.state || '',
                zip: applicant.address?.zip || '',
                availability: availabilityForEdit,
                vehicleType: applicant.vehicleType || '',
                compensationType: applicant.compensationType || '',
                timezone: userTimezone
            });
        }
    }, [applicant]);


    const handleEditChange = (e) => {
        setEditForm({ ...editForm, [e.target.name]: e.target.value });
    };

    const normalizeAvailabilityForSave = () => {
        const dayMap = {
            Sunday: 0,
            Monday: 1,
            Tuesday: 2,
            Wednesday: 3,
            Thursday: 4,
            Friday: 5,
            Saturday: 6,
        };

        return Object.entries(editForm.availability || {}).flatMap(([day, slots]) =>
            (slots || []).map(slot => ({
                dayOfWeek: dayMap[day],
                startTimeUTC: slot.start || slot.startTimeUTC,
                endTimeUTC: slot.end || slot.endTimeUTC,
            }))
        );
    };

    const handleEditSubmit = async () => {
        try {
            const updatedData = {
                ...editForm,
                address: {
                    city: editForm.city,
                    state: editForm.state,
                    zip: editForm.zip,
                },
                availability: normalizeAvailabilityForSave(),
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            };
            // Remove the flat fields so they don't get sent as duplicates
            delete updatedData.city;
            delete updatedData.state;
            delete updatedData.zip;

            await axios.put(`${API_BASE_URL}/api/applicants/${id}/update`, updatedData, { headers: await staffAuthHeaders() });
            setEditDialogOpen(false);
            fetchApplicant();
        } catch (error) {
            console.error('Error updating applicant:', error);
            const notice = staffSignInNotice(error);
            if (notice) alert(notice);
        }
    };



    useEffect(() => {
        fetchApplicant();
    }, [id]);

    const fetchApplicant = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/applicants/${id}`, { headers: await staffAuthHeaders() });
            setApplicant(response.data);
        } catch (error) {
            console.error('Error fetching applicant:', error);
            const notice = staffSignInNotice(error);
            if (notice) alert(notice);
        }
    };

    const openDeleteDialog = () => {
        setDeleteDialogOpen(true);
    };

    const closeDeleteDialog = () => {
        setDeleteDialogOpen(false);
    };

    const confirmDelete = async () => {
        try {
            await axios.delete(`${API_BASE_URL}/api/applicants/${id}`, { headers: await staffAuthHeaders() });
            navigate('/applicants'); // Redirect to applicants list after deletion
        } catch (error) {
            console.error('Error deleting applicant:', error);
            const notice = staffSignInNotice(error);
            if (notice) alert(notice);
        }
    };

    if (!applicant) return <Typography color="white">Loading...</Typography>;

    const handleAvailabilityChange = (day, selectedTimes) => {
        const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        const utcTimes = selectedTimes.map(timeSlot => {
            const [start, end] = timeSlot.split(" - ");
            return {
                start: moment.tz(start.trim(), "h:mm A", userTimezone).utc().format("HH:mm"),
                end: moment.tz(end.trim(), "h:mm A", userTimezone).utc().format("HH:mm"),
                timezone: userTimezone
            };
        });

        setEditForm(prevForm => ({
            ...prevForm,
            availability: {
                ...prevForm.availability,
                [day]: utcTimes.length > 0 ? utcTimes : []
            },
            timezone: userTimezone
        }));
    };

// ✅ UPDATED FUNCTION to call the new backend endpoint and trigger the modal
    const handleInitiateBackgroundCheck = async () => {
        setIsInitiating(true); // Show a loading state if you want
        try {
            // This now calls your backend to send the invitation email
            await axios.post(`${API_BASE_URL}/api/applicants/${applicant._id}/invite-background-check`, {}, { headers: await staffAuthHeaders() });

            // On success, show a confirmation modal (you will need to create this state)
            // setConfirmationModalOpen(true);
            alert(`Invitation sent to ${applicant.name}!`); // Using a simple alert for now

            fetchApplicant(); // Re-fetch the applicant to show the updated "Invited" status
        } catch (error) {
            console.error("Error sending invitation:", error);
            alert(staffSignInNotice(error) || 'Failed to send invitation. Please try again.');
        } finally {
            setIsInitiating(false);
        }
    };

// This can now be safely deleted as its logic is inside BackgroundCheckCell
// const getStatusChip = (status) => { ... };

// ✅ UPDATED COMPONENT with the 'invited' status
    const BackgroundCheckCell = ({ status }) => {
        const statusMap = {
            invited:  { label: 'Invited', sx: { bgcolor: '#2196f3', color: 'white' } }, // New status
            pending:  { label: 'Pending', sx: { bgcolor: '#ff9800', color: 'black' } },
            clear:    { label: 'Clear', sx: { bgcolor: '#4caf50', color: 'white' } },
            consider: { label: 'Review', sx: { bgcolor: '#f44336', color: 'white' } },
        };

        if (status === 'not_started' || !status) {
            // The button now correctly calls the new async handler
            return (
                <Button
                    variant="contained"
                    size="small"
                    onClick={handleInitiateBackgroundCheck}
                    disabled={isInitiating} // Disable button while sending
                    sx={{
                        backgroundColor: '#12c099',
                        color: '#000',
                        fontWeight: 'bold',
                        textTransform: 'none',
                        '&:hover': { backgroundColor: '#15d9a5' },
                    }}
                >
                    {isInitiating ? <CircularProgress size={20} sx={{ color: 'black' }} /> : 'Invite to Background Check'}
                </Button>
            );
        }

        // The static chip for all other statuses
        const { label, sx } = statusMap[status];
        // A fallback for any unexpected status
        if (!label) {
            return <Chip label={status} variant="outlined" size="small" />;
        }
        return <Chip label={label} sx={sx} size="small" />;
    };

    return (
        <OverviewContainer>
            <Title>{applicant.name}</Title>
            <Paper>
                <StyledTable>
                    <TableBody>
                        <TableRow>
                            <TableHeaderCell>Email</TableHeaderCell>
                            <TableBodyCell>{applicant.email}</TableBodyCell>
                        </TableRow>
                        <TableRow>
                            <TableHeaderCell>Phone</TableHeaderCell>
                            <TableBodyCell>{applicant.phone}</TableBodyCell>
                        </TableRow>
                        <TableRow>
                            <TableHeaderCell>Role</TableHeaderCell>
                            <TableBodyCell>{applicant.role}</TableBodyCell>
                        </TableRow>
                        <TableRow>
                            <TableHeaderCell>Hourly Rate</TableHeaderCell>
                            <TableBodyCell>${applicant.hourlyRate}/hr</TableBodyCell>
                        </TableRow>
                        <TableRow>
                            <TableHeaderCell>City, State, Zip</TableHeaderCell>
                            <TableBodyCell>{`${applicant.address?.city || ''}, ${applicant.address?.state || ''}, ${applicant.address?.zip || ''}`}</TableBodyCell>

                        </TableRow>
                        <TableRow>
                            <TableHeaderCell>Waiver Signed</TableHeaderCell>
                            <TableBodyCell>{applicant.waiverSigned ? 'Yes' : 'No'}</TableBodyCell>
                        </TableRow>
                        <TableRow>
                            <TableHeaderCell>Waiver URL</TableHeaderCell>
                            <TableBodyCell>
                                {applicant.waiverUrl ? (
                                    <a href={applicant.waiverUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#12c099' }}>
                                        View Waiver
                                    </a>
                                ) : (
                                    'Not Available'
                                )}
                            </TableBodyCell>
                        </TableRow>
                        <TableRow>
                            <TableHeaderCell>Payment Method</TableHeaderCell>
                            <TableBodyCell>{applicant.paymentDetails?.join(', ') || 'Not Provided'}</TableBodyCell>
                        </TableRow>
                        <TableRow>
                            <TableHeaderCell>Experience</TableHeaderCell>
                            <TableBodyCell>{applicant.experience || 'Not Provided'}</TableBodyCell>
                        </TableRow>
                        <TableRow>
                            <TableHeaderCell>Education</TableHeaderCell>
                            <TableBodyCell>
                                {applicant.education?.college?.length > 0
                                    ? applicant.education.college.map((edu) => (
                                        <div key={edu.school}>{edu.school} - {edu.degree}</div>
                                    ))
                                    : 'Not Provided'}
                            </TableBodyCell>
                        </TableRow>
                        <TableRow>
                            <TableHeaderCell>Availability</TableHeaderCell>
                            <TableBodyCell>
                                <ul style={{ paddingLeft: '20px', margin: 0 }}>
                                    {(applicant.availability && applicant.availability.length > 0) ? (
                                        applicant.availability.map((slot, index) => {
                                            const dayMap = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
                                            const displayTimezone = applicant.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
                                            return (
                                                <li key={index} style={{ listStyleType: 'none', marginBottom: '4px' }}>
                                                    <strong style={{ color: '#12c099' }}>{dayMap[slot.dayOfWeek]}:</strong>
                                                    {` ${moment.utc(slot.startTimeUTC, "HH:mm").tz(displayTimezone).format("h:mm A")} - ${moment.utc(slot.endTimeUTC, "HH:mm").tz(displayTimezone).format("h:mm A")}`}
                                                </li>
                                            );
                                        })
                                    ) : (
                                        'Not Available'
                                    )}
                                </ul>
                            </TableBodyCell>
                        </TableRow>
                        <TableRow>
                            <TableHeaderCell>CPR Certified</TableHeaderCell>
                            <TableBodyCell>{applicant.cprCertified}</TableBodyCell>
                        </TableRow>
                        <TableRow>
                            <TableHeaderCell>Authorized to Work in US</TableHeaderCell>
                            <TableBodyCell>{applicant.authorizedToWorkInUS}</TableBodyCell>
                        </TableRow>
                        <TableRow>
                            <TableHeaderCell>Date Applied</TableHeaderCell>
                            <TableBodyCell>{new Date(applicant.dateApplied).toLocaleDateString()}</TableBodyCell>
                        </TableRow>
                        <TableRow>
                            <TableHeaderCell>Background Check</TableHeaderCell>
                            <TableBodyCell>
                                <BackgroundCheckCell status={applicant.backgroundCheckStatus} />
                            </TableBodyCell>
                        </TableRow>
                    </TableBody>
                </StyledTable>
            </Paper>

            {/* Buttons */}
            <Box display="flex" justifyContent="space-between" mt={3}>
                <Button variant="contained" color="primary" href="/applicants">
                    Back to Applicants
                </Button>
                <Button variant="contained" color="primary" onClick={() => setEditDialogOpen(true)}>
                    Edit Applicant
                </Button>
                <Button variant="contained" color="error" onClick={openDeleteDialog}>
                    Delete Applicant
                </Button>
            </Box>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog}>
                <DialogTitle>Are you sure you want to delete this applicant?</DialogTitle>
                <DialogActions>
                    <Button onClick={closeDeleteDialog} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={confirmDelete} color="secondary">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)}>
                <DialogTitle>Edit Applicant</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Name"
                        name="name"
                        value={editForm.name}
                        onChange={handleEditChange}
                        fullWidth
                        margin="dense"
                    />

                    <TextField
                        label="Email"
                        name="email"
                        value={editForm.email}
                        onChange={handleEditChange}
                        fullWidth
                        margin="dense"
                    />

                    <TextField
                        label="Phone"
                        name="phone"
                        value={editForm.phone}
                        onChange={handleEditChange}
                        fullWidth
                        margin="dense"
                    />

                    {/* 🔒 ROLE — DISPLAY ONLY */}
                    <TextField
                        label="Role"
                        value={editForm.role}
                        fullWidth
                        margin="dense"
                        disabled
                    />

                    <TextField
                        label="Hourly Rate"
                        name="hourlyRate"
                        type="number"
                        value={editForm.hourlyRate}
                        onChange={handleEditChange}
                        fullWidth
                        margin="dense"
                    />

                    <TextField
                        label="City"
                        name="city"
                        value={editForm.city}
                        onChange={handleEditChange}
                        fullWidth
                        margin="dense"
                    />

                    <TextField
                        label="State"
                        name="state"
                        value={editForm.state}
                        onChange={handleEditChange}
                        fullWidth
                        margin="dense"
                    />

                    <TextField
                        label="Zip"
                        name="zip"
                        value={editForm.zip}
                        onChange={handleEditChange}
                        fullWidth
                        margin="dense"
                    />

                    <Typography variant="h6" sx={{ marginTop: '1rem', color: '#12c099' }}>
                        Availability
                    </Typography>
                    {Object.entries(editForm.availability || {}).map(([day, times]) => (
                        <FormControl key={day} fullWidth sx={{ marginBottom: '1rem' }}>
                            <InputLabel>{day.charAt(0).toUpperCase() + day.slice(1)}</InputLabel>
                            <Select
                                multiple
                                value={times.map(slot => `${slot.start} - ${slot.end}`)} // ✅ Convert object to string
                                onChange={(e) => handleAvailabilityChange(day, e.target.value)}
                                renderValue={(selected) => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {selected.map((value) => (
                                            <Chip key={value} label={value} onDelete={() => {
                                                const updatedTimes = selected.filter(t => t !== value);
                                                handleAvailabilityChange(day, updatedTimes);
                                            }} />
                                        ))}
                                    </Box>
                                )}
                            >
                                {[
                                    "8:00 AM - 10:00 AM",
                                    "9:00 AM - 11:00 AM",
                                    "10:00 AM - 12:00 PM",
                                    "12:00 PM - 2:00 PM",
                                    "2:00 PM - 4:00 PM",
                                    "4:00 PM - 6:00 PM",
                                    "6:00 PM - 8:00 PM"
                                ].map((timeSlot) => (
                                    <MenuItem key={timeSlot} value={timeSlot}>
                                        {timeSlot}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    ))}

                    {/* ================= COURIER-ONLY FIELDS ================= */}
                    {editForm.role?.toLowerCase() === 'courier' && (
                        <>
                            <Typography
                                variant="h6"
                                sx={{ mt: 2, color: '#12c099' }}
                            >
                                Courier Details
                            </Typography>

                            <FormControl fullWidth margin="dense">
                                <InputLabel>Vehicle Type</InputLabel>
                                <Select
                                    name="vehicleType"
                                    value={editForm.vehicleType}
                                    onChange={handleEditChange}
                                >
                                    <MenuItem value="Car">Car</MenuItem>
                                    <MenuItem value="SUV">SUV</MenuItem>
                                    <MenuItem value="Van">Van</MenuItem>
                                    <MenuItem value="Truck">Truck</MenuItem>
                                    <MenuItem value="Bike">Bike</MenuItem>
                                    <MenuItem value="Scooter">Scooter</MenuItem>
                                </Select>
                            </FormControl>

                            <FormControl fullWidth margin="dense">
                                <InputLabel>Compensation Model</InputLabel>
                                <Select
                                    name="compensationType"
                                    value={editForm.compensationType}
                                    onChange={handleEditChange}
                                >
                                    <MenuItem value="Hourly">Hourly</MenuItem>
                                    <MenuItem value="PerDelivery">Per Delivery</MenuItem>
                                </Select>
                            </FormControl>

                        </>
                    )}

                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditDialogOpen(false)} color="primary">Cancel</Button>
                    <Button onClick={handleEditSubmit} color="secondary">Save</Button>
                </DialogActions>
            </Dialog>;
        </OverviewContainer>
    );
};

export default ApplicantOverview;
